import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import { useSeo } from '@/hooks/useSeo';
import { seiyuuList } from '@/mocks/seiyuu';
import { characters } from '@/mocks/characters';

export default function SeiyuuPage() {
  useSeo({
    title: '声优表 - 妖精动漫配音演员一览 | 妖精动漫网',
    description: '妖精动漫网声优表，整理妖精、精灵类动漫作品的配音演员信息，包括出道年份、所属机构、擅长声线与代表角色。按角色名或声优名快速检索。',
  });

  const [gender, setGender] = useState<'全部' | '女' | '男'>('全部');
  const [kw, setKw] = useState('');
  const [input, setInput] = useState('');

  const filtered = useMemo(() => {
    return seiyuuList.filter((s) => {
      const matchGender = gender === '全部' || s.gender === gender;
      const q = kw.trim().toLowerCase();
      const matchKw =
        !q ||
        s.name.toLowerCase().includes(q) ||
        s.specialty.toLowerCase().includes(q) ||
        s.representative.some((r) => r.role.toLowerCase().includes(q) || r.work.toLowerCase().includes(q));
      return matchGender && matchKw;
    });
  }, [gender, kw]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setKw(input);
  };

  const countFor = (g: '女' | '男') => seiyuuList.filter((s) => s.gender === g).length;

  return (
    <PageLayout>
      <section className="relative overflow-hidden pt-16 md:pt-18">
        <img
          src="https://readdy.ai/api/search-image?query=stylized%20artistic%20banner%20with%20microphone%20and%20sound%20waves%20in%20magical%20emerald%20and%20gold%20tones%2C%20floating%20sparkles%2C%20soft%20painterly%20fantasy%20atmosphere%2C%20clean%20minimal%20composition&width=1600&height=400&seq=seiyuu-banner&orientation=landscape"
          alt="金色与翠绿色调的话筒与音浪奇幻背景"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/85 to-primary-800/75" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <nav aria-label="面包屑" className="mb-3 flex items-center gap-1.5 text-xs text-background-100/80">
            <Link to="/" className="hover:text-accent-300">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">声优表</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">声优表</h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base leading-6 text-background-100/90">
            集中整理妖精、精灵类动漫作品的配音演员资料。想确认某位角色的配音演员，或了解声优的声线与代表角色，在这里直接查。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-10">
        {/* 搜索与筛选 */}
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <form onSubmit={submit} className="flex flex-1 max-w-md gap-2">
            <div className="flex flex-1 items-center gap-2 rounded-full border border-background-200/70 bg-background-50 px-4 py-2.5 focus-within:border-primary-300">
              <i className="ri-search-line text-foreground-400" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入声优名或代表角色…"
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-foreground-400"
                aria-label="搜索声优"
              />
            </div>
            <button type="submit" className="rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap hover:bg-primary-600 transition-colors">
              搜索
            </button>
          </form>

          <div className="flex items-center gap-2 rounded-full bg-background-100 p-1">
            {(['全部', '女', '男'] as const).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGender(g)}
                className={`rounded-full px-4 py-1.5 text-sm whitespace-nowrap transition-colors ${
                  gender === g ? 'bg-primary-500 text-background-50' : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                {g}{g !== '全部' && <span className="ml-1 text-xs opacity-70">{countFor(g)}</span>}
              </button>
            ))}
          </div>
        </div>

        <p className="mb-5 text-sm text-foreground-600">共收录 <strong className="text-primary-700">{filtered.length}</strong> 位配音演员</p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {filtered.map((sv) => {
            const roleCount = characters.filter((c) => c.seiyuu === sv.id).length;
            return (
              <div key={sv.id} className="flex gap-5 rounded-xl border border-background-200/70 bg-background-50 p-5">
                <img src={sv.image} alt={`声优 ${sv.name}`} className="h-24 w-24 shrink-0 rounded-full object-cover ring-2 ring-accent-200" loading="lazy" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-heading text-lg font-bold text-foreground-950">{sv.name}</h2>
                    <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${sv.gender === '女' ? 'bg-secondary-100 text-secondary-800' : 'bg-primary-100 text-primary-700'}`}>
                      <i className={`${sv.gender === '女' ? 'ri-women-line' : 'ri-men-line'}`} />
                      {sv.gender}
                    </span>
                  </div>
                  <p className="mt-1.5 text-xs text-foreground-500">
                    {sv.debut} 出道 · {sv.agency} · 本站收录 {roleCount} 位角色
                  </p>
                  <p className="mt-2 text-sm leading-6 text-foreground-700">{sv.introduction}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {sv.representative.map((r) => (
                      <Link
                        key={r.role}
                        to="/characters"
                        className="rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-600 hover:bg-accent-100 hover:text-accent-900 transition-colors"
                      >
                        {r.role} · {r.work}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </PageLayout>
  );
}